import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Center, Flex, Grid, Spinner, Stack, Text, Textarea } from '@chakra-ui/react';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import useCoursesStore from '@/utils/use-courses';
import { unwrap } from '@/pages/courses/api';
import { Course, CourseVersion } from '@/lib/types';

// Version history: every publish in reverse order with its note, author and
// how many learners are pinned to it. Version numbers are admin-facing only —
// learners never see them.
//
// onPublished hands the bumped course back so the page header reflects the new
// version without a refetch.
export default function VersionsTab({
  course,
  onPublished,
}: {
  course: Course;
  onPublished?: (course: Course) => void;
}) {
  const { setCourseStatus } = useCoursesStore();
  const [versions, setVersions] = useState<CourseVersion[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Version number whose snapshot is expanded, with a cache of fetched trees.
  const [openVersion, setOpenVersion] = useState<number | null>(null);
  const [snapshots, setSnapshots] = useState<Record<number, CourseVersion>>({});
  const [note, setNote] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);

  const loadVersions = useCallback(() => {
    return api
      .get(`courses/${course.id}/versions`)
      .then((res) => setVersions(unwrap<CourseVersion[]>(res) ?? []))
      .catch(() => setVersions(null));
  }, [course.id]);

  useEffect(() => {
    setIsLoading(true);
    loadVersions().finally(() => setIsLoading(false));
  }, [loadVersions]);

  // Publishing a live course never changes its status — it only appends the
  // next ledger entry. The share link keeps working throughout.
  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const published = await setCourseStatus(course.id, 'published', note.trim());
      setNote('');
      onPublished?.(published);
      await loadVersions();
      toaster.create({
        title: `Version ${published.version} published`,
        description: 'Learners keep their progress — nobody is restarted.',
        type: 'success',
      });
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to publish',
        type: 'error',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const canPublish = course.can_edit && course.status === 'published';

  const toggleSnapshot = async (version: number) => {
    if (openVersion === version) {
      setOpenVersion(null);
      return;
    }
    setOpenVersion(version);
    if (!snapshots[version]) {
      try {
        const res = await api.get(`courses/${course.id}/versions/${version}`);
        const loaded = unwrap<CourseVersion>(res);
        setSnapshots((prev) => ({ ...prev, [version]: loaded }));
      } catch {
        setOpenVersion(null);
      }
    }
  };

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size='lg' />
      </Center>
    );
  }

  if (!versions) {
    return (
      <Text fontSize='sm' color='fg.muted' textAlign='center' py={8}>
        Failed to load the version history.
      </Text>
    );
  }

  return (
    <Grid templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) 300px' }} gap={5} alignItems='start'>
      <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={6}>
        <Text
          fontFamily='heading'
          fontSize='2xs'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.16em'
          color='fg.muted'
          mb={4}
        >
          Version history
        </Text>

        {versions.length === 0 && (
          <Text fontSize='sm' color='fg.muted' py={4}>
            No versions yet — the first publish will create version 1.
          </Text>
        )}

        {versions.map((version) => {
          const snapshot = snapshots[version.version];
          const isOpen = openVersion === version.version;
          return (
            <Box key={version.id} borderTopWidth='1px' borderColor='border' py={4}>
              <Grid templateColumns='64px minmax(0,1fr) auto' gap={4} alignItems='start'>
                <Text fontFamily='heading' fontSize='sm' fontWeight='800' color='fg.strong'>
                  v{version.version}
                </Text>
                <Box minW={0}>
                  <Text fontSize='sm' color='fg'>
                    {version.note}
                  </Text>
                  <Text fontSize='xs' color='fg.muted' mt={1}>
                    {version.published_by_name || 'Unknown'} ·{' '}
                    {new Date(version.created_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}{' '}
                    · {version.module_count} modules · {version.item_count} items
                  </Text>
                  <Button
                    variant='ghost'
                    size='xs'
                    mt={1.5}
                    ml={-2}
                    onClick={() => toggleSnapshot(version.version)}
                  >
                    {isOpen ? 'Hide contents' : 'View contents'}
                  </Button>
                  {isOpen && (
                    <Box mt={2} bg='bg.subtle' borderRadius='md' px={3.5} py={3}>
                      {!snapshot ? (
                        <Spinner size='sm' />
                      ) : (
                        (snapshot.snapshot_modules ?? []).map((module, index) => (
                          <Flex key={module.id} gap={2} py={1}>
                            <Text fontSize='xs' color='fg.subtle' w='18px'>
                              {index + 1}.
                            </Text>
                            <Text fontSize='xs' color='fg' flex='1' minW={0} truncate>
                              {module.name || 'Untitled module'}
                            </Text>
                            <Text fontSize='xs' color='fg.muted'>
                              {(module.items ?? []).length}{' '}
                              {(module.items ?? []).length === 1 ? 'item' : 'items'}
                              {module.assignment_text && ' · assignment'}
                            </Text>
                          </Flex>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
                <Text fontSize='xs' color='fg.muted' whiteSpace='nowrap'>
                  {version.learner_count > 0
                    ? `${version.learner_count} ${
                        version.learner_count === 1 ? 'learner' : 'learners'
                      } on this version`
                    : ''}
                </Text>
              </Grid>
            </Box>
          );
        })}
      </Box>

      <Stack gap={5}>
        {canPublish && (
          <Box
            bg='bg.panel'
            borderWidth='1px'
            borderColor='border'
            borderRadius='lg'
            shadow='sm'
            p={5}
          >
            <Text
              fontFamily='heading'
              fontSize='2xs'
              fontWeight='700'
              textTransform='uppercase'
              letterSpacing='0.16em'
              color='fg.muted'
              mb={2.5}
            >
              Publish new version
            </Text>
            <Text fontSize='sm' color='fg.muted' mb={3.5}>
              Content edits already apply to everyone. Publishing records version{' '}
              {course.version + 1} in the history so the roster can tell who finished on which
              version.
            </Text>
            <Field label='Version note' helperText='Optional.' mb={3.5}>
              <Textarea
                rows={2}
                maxLength={500}
                value={note}
                placeholder='What changed? e.g. Swapped the intro video.'
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            <Button
              variant='primary'
              size='sm'
              width='full'
              onClick={handlePublish}
              loading={isPublishing}
            >
              Publish version {course.version + 1}
            </Button>
          </Box>
        )}

        <Box
          bg='bg.panel'
          borderWidth='1px'
          borderColor='border'
          borderRadius='lg'
          shadow='sm'
          p={5}
        >
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            color='fg.muted'
            mb={2.5}
          >
            How versions behave
          </Text>
          <Text fontSize='sm' color='fg.muted' mb={2.5}>
            The shared link always opens the current published content. Editing a published course
            updates it for everyone, including learners partway through.
          </Text>
          <Text fontSize='sm' color='fg.muted' mb={2.5}>
            Every completion record is tagged with the version the learner started on, so the
            tracking roster can say who finished v1 after v2 ships.
          </Text>
          <Text fontSize='sm' color='fg.muted'>
            Version numbers are admin-only — learners never see them.
          </Text>
        </Box>
      </Stack>
    </Grid>
  );
}
